import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { SERVER_URL } from '../../api/url';

const ZoomQuotationForm = ({ courseID }) => {
  const [file, setFile] = useState(null);
  // Renamed setError to setFormError to avoid conflict with your local state
  const { register, trigger, reset, handleSubmit, setError: setFormError, formState: { errors } } = useForm();
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('email', data.email);
      formData.append('phNumber', data.phNumber);
      formData.append('fbName', data.fbName);
      formData.append('age', data.age);
      formData.append('courseType', data.courseType);
      formData.append('ruleStatus', data.ruleStatus ? 'true' : 'false');

      const communicationPreferences = {
        telegram: data.communication?.telegram || false,
        viber: data.communication?.viber || false,
        whatsapp: data.communication?.whatsapp || false,
        imessage: data.communication?.imessage || false,
        other: data.communication?.other || false,
      };

      formData.append('communication', JSON.stringify(communicationPreferences));
      
      if (file) {
        formData.append('snapShot', file);
      } else {
        setFormError('snapShot', { type: 'manual', message: 'payment screenshot is required' });
        setLoading(false);
        return;
      }

      const response = await axios.post(`${SERVER_URL}/zoom-form/upload-form/${courseID}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setMessage(response?.data?.data || "submission successful!");
      setError("");
      reset(); 
      setFile(null);
      setLoading(false);
    } catch (err) {
      setError('there was an error submitting the form.');
      setMessage("");
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Allowed types: jpg, jpeg, png
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    
    if (!allowedTypes.includes(selectedFile.type)) {
      // Fixed: changed from setManualError to setFormError
      setFormError('snapShot', { 
        type: 'manual', 
        message: 'Only .jpg, .jpeg, and .png files are accepted' 
      });
      setFile(null);
      e.target.value = null; 
      return;
    }

    setFile(selectedFile);
    trigger('snapShot'); 
  };

  return (
    <div className="container-fluid p-0">
      <style>{`
        .full-width-form { width: 100%; }
        .custom-input {
          width: 100% !important;
          border: none;
          border-bottom: 1px solid #ccc;
          border-radius: 0;
          padding: 10px 0;
          box-shadow: none !important;
          outline: none !important;
          background-image: none !important; 
        }
        .custom-input.is-invalid { border-bottom: 1px solid #dc3545 !important; }
        .error-message {
          color: #dc3545;
          font-size: 11px;
          margin-top: 5px;
          display: block;
          text-align: left;
        }
        .require-star { color: #dc3545; }
        .input-label { font-weight: bold; margin-bottom: 5px; display: block; width: 100%; }
        .input-container { width: 100%; margin-bottom: 10px; }
        .submit-btn { width: 100%; padding: 12px; }
      `}</style>

      <form className="d-flex flex-column gap-4 full-width-form" onSubmit={handleSubmit(onSubmit)}>

        {/* Full Name */}
        <div className="input-container">
          <label className="input-label">Full Name: <span className="require-star">*</span></label>
          <input
            type="text"
            className={`form-control custom-input ${errors.name ? 'is-invalid' : ''}`}
            {...register('name', { 
              required: 'name is required',
              minLength: { value: 4, message: 'name must be at least 4 characters' }
            })} 
          />
          {errors.name && <span className="error-message">{errors.name.message}</span>}
        </div>

        {/* Email */}
        <div className="input-container">
          <label className="input-label">Email address: <span className="require-star">*</span></label>
          <input
            type="text"
            className={`form-control custom-input ${errors.email ? 'is-invalid' : ''}`}
            {...register('email', { 
              required: 'email is required',
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                message: 'invalid email format'
              }
            })} 
          />
          {errors.email && <span className="error-message">{errors.email.message}</span>}
        </div>

        {/* Phone Number */}
        <div className="input-container">
          <label className="input-label">Phone Number: <span className="require-star">*</span></label>
          <input
            type="text"
            className={`form-control custom-input ${errors.phNumber ? 'is-invalid' : ''}`}
            {...register('phNumber', { required: 'phone number is required' })} 
          />
          {errors.phNumber && <span className="error-message">{errors.phNumber.message}</span>}
        </div>

        {/* Facebook Name */}
        <div className="input-container">
          <label className="input-label">Facebook Name: <span className="require-star">*</span></label>
          <input
            type="text"
            className={`form-control custom-input ${errors.fbName ? 'is-invalid' : ''}`}
            {...register('fbName', { required: 'facebook name is required' })} 
          />
          {errors.fbName && <span className="error-message">{errors.fbName.message}</span>}
        </div>

        {/* Age */}
        <div className="input-container">
          <label className="input-label">Age: <span className="require-star">*</span></label>
          <input
            type="number"
            className={`form-control custom-input ${errors.age ? 'is-invalid' : ''}`}
            {...register('age', { 
              required: 'age is required',
              min: { value: 5, message: 'minimum age is 5' },
              max: { value: 90, message: 'maximum age is 90' }
            })} 
          />
          {errors.age && <span className="error-message">{errors.age.message}</span>}
        </div>

        {/* Course Type */}
        <div className='input-container'>
          <label className="input-label">Course Type: <span className="require-star">*</span></label>
          <div className="d-flex flex-column mt-2">
            <label className='d-flex gap-2 align-items-center'><input type="checkbox" value="Rules & Policy for the Zoom Group Course" {...register('courseType', { required: 'course type is required' })} />Rules & Policy for the Zoom Group Course</label>
            <label className='d-flex gap-2 align-items-center'><input type="checkbox" value="Rules & Policy for the Zoom Private/Individual Course" {...register('courseType', { required: 'course type is required' })} />Rules & Policy for the Zoom Private/Individual Course</label>
          </div>
          {errors.courseType && <span className="error-message">{errors.courseType.message}</span>}
        </div>

        <div className="input-container">
          <label className="input-label">Payment Screenshot: <span className="require-star">*</span></label>
          <input
            type="file"
            className={`form-control custom-input ${errors.snapShot ? 'is-invalid' : ''}`}
            accept=".jpg, .jpeg, .png" 
            {...register('snapShot', { 
              required: 'payment screenshot is required',
              onChange: (e) => handleFileChange(e) 
            })}
          />
          {errors.snapShot && <span className="error-message">{errors.snapShot.message}</span>}
        </div>

        {/* Communication */}
        <div className="input-container">
          <label className="input-label">Communication: <span className="require-star">*</span></label>
          <div className="d-flex flex-column mt-2">
            <label className='d-flex gap-2 align-items-center'>
              <input type="checkbox" {...register('communication.telegram', {
                validate: (v, vals) => Object.values(vals.communication || {}).some(Boolean) || "communication is required"
              })} /> Telegram
            </label>
            <label className='d-flex gap-2 align-items-center'><input type="checkbox" {...register('communication.viber')} onChange={() => trigger('communication.telegram')} /> Viber</label>
            <label className='d-flex gap-2 align-items-center'><input type="checkbox" {...register('communication.whatsapp')} onChange={() => trigger('communication.telegram')} /> WhatsApp</label>
            <label className='d-flex gap-2 align-items-center'><input type="checkbox" {...register('communication.imessage')} onChange={() => trigger('communication.telegram')} /> iMessage</label>
            <label className='d-flex gap-2 align-items-center'><input type="checkbox" {...register('communication.other')} onChange={() => trigger('communication.telegram')} /> Other</label>
          </div>
          {errors.communication?.telegram && <span className="error-message">{errors.communication.telegram.message}</span>}
        </div>

        {/* Accept Rules */}
        <div className="input-container">
          <label className='d-flex gap-2 align-items-center'>
            <input type="checkbox" {...register('ruleStatus', { required: 'accepting rules is required' })} />
            <span>Accept Rules <span className="require-star">*</span></span>
          </label>
          {errors.ruleStatus && <span className="error-message">{errors.ruleStatus.message}</span>}
        </div>

        {message && <div className="alert alert-info">{message}</div>}
         {error && (
          <div className="alert alert-danger">
            {error}
          </div>
        )}

      <button type="submit" className="btn btn-primary btn-lg px-4" disabled={loading}>
          {loading ? "Submitting..." : "Submit"}
        </button>
      </form>
    </div>
  );
};

export default ZoomQuotationForm;